import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle2, Trophy, Star, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackEvent } from '../utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable CourseQuiz component for all courses
 * @param {string} quizId - Unique quiz identifier (e.g. 'menschen_a1_quiz')
 * @param {string} courseId - Course identifier (e.g. 'menschen_a1')
 * @param {string} title - Quiz title
 * @param {Array} sections - Array of { label, questions: [{ id, q, qAr, options, correct }] }
 * @param {Object} answers - Map of question id -> correct option index
 * @param {boolean} isActive - Whether this tab is currently active
 */
export function CourseQuiz({ quizId, courseId, title, sections, answers, isActive }) {
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [previousBest, setPreviousBest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [xpEarned, setXpEarned] = useState(0);

  useEffect(() => {
    const fetchPrev = async () => {
      try {
        const res = await axios.get(`${API}/quiz/results`);
        const found = (res.data.results || []).find(r => r.quiz_id === quizId);
        if (found) setPreviousBest(found);
      } catch {}
    };
    fetchPrev();
  }, [quizId]);

  const totalQuestions = Object.keys(answers).length;

  const calculateScore = () => {
    let correct = 0;
    Object.keys(answers).forEach(qId => {
      if (userAnswers[qId] === answers[qId]) correct++;
    });
    return { correct, total: totalQuestions, percentage: Math.round((correct / totalQuestions) * 100) };
  };

  const handleAnswer = (qId, idx) => {
    if (showResults) return;
    setUserAnswers(prev => ({ ...prev, [qId]: idx }));
  };

  const submitQuiz = async () => {
    setShowResults(true);
    const score = calculateScore();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quiz/submit`, {
        quiz_id: quizId,
        course_id: courseId,
        correct: score.correct,
        total: score.total,
        percentage: score.percentage
      });
      setXpEarned(res.data.xp_earned || 0);
      trackEvent('quiz_completed', { course_id: courseId, quiz_id: quizId, score: score.percentage, xp: res.data.xp_earned || 0 });
      if (res.data.is_new_best) {
        setPreviousBest(prev => ({
          ...prev,
          best_percentage: score.percentage,
          best_correct: score.correct,
          percentage: score.percentage
        }));
        toast.success(`New best score! +${res.data.xp_earned} XP`);
      } else {
        toast.info(`+${res.data.xp_earned} XP earned`);
      }
    } catch {
      toast.error('Could not save quiz result');
    } finally {
      setSubmitting(false);
    }
  };

  const resetQuiz = () => {
    setUserAnswers({});
    setShowResults(false);
    setXpEarned(0);
  };

  if (!isActive) return null;

  return (
    <div data-testid={`quiz-${quizId}`}>
      <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px' }}>{title}</h2>
      <p style={{ color: '#94a3b8', marginBottom: '20px' }}>
        {totalQuestions} Questions · <span className="ar">{totalQuestions} سؤالاً</span>
      </p>

      {previousBest && !showResults && (
        <div className="flex items-center gap-3 p-4 rounded-xl mb-4" style={{
          background: 'linear-gradient(135deg, rgba(20,184,166,0.15), rgba(20,184,166,0.05))',
          border: '1px solid rgba(20,184,166,0.3)'
        }}>
          <CheckCircle2 className="h-6 w-6 text-teal-400 flex-shrink-0" />
          <div className="flex-1">
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>Quiz Achieved</p>
            <p style={{ fontSize: '12px', color: '#94a3b8' }}>
              Best: {previousBest.best_percentage || previousBest.percentage}%
              ({previousBest.best_correct || previousBest.correct}/{totalQuestions} correct)
            </p>
          </div>
          <Badge className="bg-teal-500 text-white">
            <Trophy className="h-3 w-3 mr-1" />
            {previousBest.best_percentage || previousBest.percentage}%
          </Badge>
        </div>
      )}

      {sections.map((section, si) => (
        <div key={`section-${si}`}>
          {section.label && (
            <div className={`quiz-label${si === 1 ? ' b' : si === 2 ? ' c' : ''}`}>
              {section.label}
            </div>
          )}
          {section.questions.map((qq, qi) => (
            <div key={qq.id} className="quiz-question">
              <p>
                <strong>{qq.q}</strong>
                {qq.qAr && <><br /><span className="ar">{qq.qAr}</span></>}
              </p>
              <div className="quiz-options">
                {qq.options.map((opt, oi) => {
                  let cls = 'quiz-option';
                  if (userAnswers[qq.id] === oi) cls += ' selected';
                  if (showResults) {
                    if (oi === answers[qq.id]) cls += ' correct';
                    else if (userAnswers[qq.id] === oi) cls += ' wrong';
                  }
                  return (
                    <div
                      key={`${qq.id}-${oi}`}
                      className={cls}
                      onClick={() => handleAnswer(qq.id, oi)}
                      data-testid={`quiz-${qq.id}-opt-${oi}`}
                    >
                      {String.fromCharCode(65 + oi)}) {opt}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ))}

      {!showResults ? (
        <div className="space-y-3 mt-6">
          <p style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center' }}>
            {Object.keys(userAnswers).length} / {totalQuestions} answered
          </p>
          <Button
            onClick={submitQuiz}
            className="w-full bg-teal-600 hover:bg-teal-700"
            disabled={Object.keys(userAnswers).length === 0 || submitting}
            data-testid="quiz-submit-btn"
          >
            {submitting ? 'Saving...' : 'Check Answers · Antworten prüfen · تحقق من الإجابات'}
          </Button>
        </div>
      ) : (
        <div className="quiz-results" data-testid="quiz-results">
          <CheckCircle2 className="h-8 w-8 mx-auto text-teal-400 mb-2" style={{ display: 'block' }} />
          <div className="quiz-score">{calculateScore().percentage}%</div>
          <div className="quiz-score-text">
            {calculateScore().correct} / {calculateScore().total} correct
          </div>
          {xpEarned > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '12px' }}>
              <Star className="h-5 w-5 text-amber-400" />
              <span style={{ color: '#fbbf24', fontWeight: '700', fontSize: '18px' }}>+{xpEarned} XP</span>
            </div>
          )}
          <div className="quiz-message">
            {calculateScore().percentage >= 80 ? 'Ausgezeichnet! Excellent! ممتاز!' :
             calculateScore().percentage >= 60 ? 'Gut gemacht! Good job! أحسنت!' :
             'Weiter üben! Keep practicing! استمر في التدريب!'}
          </div>
          <Button onClick={resetQuiz} variant="outline" className="mt-4" data-testid="quiz-reset-btn">
            <RotateCcw className="h-4 w-4 mr-2" />
            Nochmal · Try again · <span className="ar">حاول مجدداً</span>
          </Button>
        </div>
      )}
    </div>
  );
}
